import React from 'react';
import { calculateOverallProgress, deriveWorkflowType } from '../utils/orchestrationConfigs';
import './ProgressBar.css';

/**
 * ProgressBar component for displaying progress with step indicators
 *
 * @param {Object} props - Component props
 * @param {number} props.percentage - Current progress percentage (0-100)
 * @param {string} props.currentStep - Current step identifier
 * @param {Array} props.steps - Array of step identifiers in order
 * @param {Object} props.stepLabels - Object mapping step identifiers to display labels
 * @param {Object} props.orchestrationData - Optional orchestration data for detecting skipped steps
 * @param {Object} props.message - Optional message object for workflow-specific logic
 *
 * Example configurations:
 *
 * Fast Track:
 * steps: ['campaign-processing', 'creative-processing', 'launcher-processing', 'api-processing']
 * stepLabels: {
 *   'campaign-processing': 'Processing IO',
 *   'creative-processing': 'Processing Creatives',
 *   'launcher-processing': 'Applying Configuration',
 *   'api-processing': 'Calling Channel API'
 * }
 *
 * Salesforce Orchestration:
 * steps: ['campaign', 'launcher', 'salesforce']
 * stepLabels: {
 *   'campaign': 'Processing IO',
 *   'launcher': 'Configuring Campaign',
 *   'salesforce': 'Creating Opportunity'
 * }
 */
const ProgressBar = ({ percentage, currentStep, steps, stepLabels, orchestrationData, message }) => {
  // Calculate corrected progress percentage if we have orchestration data
  let correctedPercentage = percentage;
  if (orchestrationData && message && (message.action === 'fast-track' || message.action === 'fast-track-shell')) {
    const workflowType = deriveWorkflowType(message);
    correctedPercentage = calculateOverallProgress(workflowType, orchestrationData);
  }

  // Check if any steps are skipped
  const hasSkippedSteps = steps.some(step => {
    if (orchestrationData && message) {
      if ((message.action === 'fast-track' || message.action === 'fast-track-shell') &&
          step === 'creative-processing' &&
          orchestrationData.creative_status === 'skipped') {
        return true;
      }
    }
    return currentStep === 'creative-skipped' && step === 'creative-processing';
  });

  // Calculate step width based on number of steps
  const getStepWidth = () => {
    return `${100 / steps.length}%`;
  };

  // Determine which steps are active, completed, pending, or skipped
  const getStepStatus = (stepName) => {
    // Check for skipped steps using orchestration data
    if (orchestrationData && message) {
      // For fast-track workflows, check if creative step is skipped
      if ((message.action === 'fast-track' || message.action === 'fast-track-shell') &&
          stepName === 'creative-processing' &&
          orchestrationData.creative_status === 'skipped') {
        return 'skipped';
      }
    }

    // Handle special current step cases for skipped steps
    if (currentStep === 'creative-skipped' && stepName === 'creative-processing') {
      return 'skipped';
    }

    // If this is the current step, it's active
    if (currentStep === stepName) return 'active';

    // If the current step comes after this step in the array, this step is completed
    const currentStepIndex = steps.indexOf(currentStep);
    const thisStepIndex = steps.indexOf(stepName);

    // Handle special cases where current step indicates a previous step was skipped
    if ((currentStep === 'creative-skipped' ||
         currentStep === 'launcher-processing' ||
         currentStep === 'launcher-completed' ||
         currentStep === 'api-processing' ||
         currentStep === 'completed') &&
        stepName === 'creative-processing' &&
        orchestrationData &&
        orchestrationData.creative_status === 'skipped') {
      return 'skipped';
    }

    // Standard completion check
    if (currentStepIndex > thisStepIndex && currentStepIndex !== -1 && thisStepIndex !== -1) {
      return 'completed';
    }

    // Otherwise, it's pending
    return 'pending';
  };

  return (
    <div className="progress-bar-container">
      <div className="progress-bar">
        <div
          className="progress-bar-fill"
          style={{ width: `${correctedPercentage}%` }}
        ></div>
      </div>
      <div className="progress-percentage">{Math.round(correctedPercentage)}%</div>
      
      <div className={`progress-steps ${hasSkippedSteps ? 'has-skipped' : ''}`}>
        {steps.map((step, index) => {
          const stepStatus = getStepStatus(step);
          return (
            <div
              key={index}
              className={`progress-step ${stepStatus}`}
              style={{ width: getStepWidth() }}
            >
              <div className="step-indicator">
                {stepStatus === 'completed' && <span className="step-icon">✓</span>}
                {stepStatus === 'skipped' && <span className="step-icon">—</span>}
                {stepStatus === 'active' && <span className="step-icon">●</span>}
              </div>
              <div
                className="step-label"
                title={stepStatus === 'skipped' ? 'This step was skipped for this workflow type' : ''}
              >
                {stepStatus === 'skipped' && !(stepLabels[step] || '').includes('Skipped')
                  ? `${stepLabels[step] || step} (Skipped)`
                  : (stepLabels[step] || step)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressBar;
